import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoading } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { RagBadge } from "@/components/common/RagBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PackManagerDialog, ApplyPackDialog } from "@/components/OnboardingPackDialogs";
import { ModuleAccessEditor } from "@/components/ModuleAccessEditor";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { fmtDate, ORG_STATUS } from "@/lib/format";
import { toast } from "sonner";

export default function Clients() {
  const { orgs, setOrgs, setOrgId } = useOrg();
  const { isSuper } = useAuth();
  const navigate = useNavigate();
  const [consultants, setConsultants] = useState([]);
  const [creating, setCreating] = useState(false);
  const [managingPacks, setManagingPacks] = useState(false);
  const [applyPackTo, setApplyPackTo] = useState(null);
  const [manageAccessFor, setManageAccessFor] = useState(null);

  useEffect(() => { api.get("/consultants").then((r) => setConsultants(r.data)).catch(() => {}); }, []);

  const open = (id) => { setOrgId(id); navigate("/"); };

  return (
    <div data-testid="clients-page">
      <PageHeader eyebrow="Amden" title="Clients" subtitle="Open a client to enter their workspace." icon="Building2"
        actions={isSuper && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setManagingPacks(true)} data-testid="manage-packs-btn"><Icons.PackageOpen size={16} className="mr-1.5" /> Onboarding packs</Button>
            <Button onClick={() => setCreating(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="new-client-btn"><Icons.Plus size={16} className="mr-1.5" /> New client</Button>
          </div>
        )} />

      {orgs.length === 0 ? <EmptyState icon="Building2" title="No clients yet" description="Create your first client organisation." action={isSuper && <Button onClick={() => setCreating(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]">New client</Button>} /> : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {orgs.map((o) => (
            <div key={o.id} onClick={() => open(o.id)} className="amden-card amden-card-hover cursor-pointer p-6" data-testid={`client-card-${o.id}`}>
              <div className="flex items-start justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0B1729] font-display text-lg font-bold text-[#FFC24D]">{o.company_name[0]}</span>
                <RagBadge rag={ORG_STATUS[o.status]?.rag}>{ORG_STATUS[o.status]?.label}</RagBadge>
              </div>
              <h3 className="mt-3 font-display text-lg font-bold text-[#0B1729]">{o.company_name}</h3>
              <p className="text-sm text-[#1E2D44]/55">{o.industry}</p>
              <div className="mt-4 space-y-1.5 border-t border-[#EEF1F6] pt-3 text-xs text-[#1E2D44]/55">
                <div className="flex items-center gap-1.5"><Icons.UserCog size={13} /> {o.consultant_name || "Unassigned"}</div>
                <div className="flex items-center gap-1.5"><Icons.Package size={13} /> {o.package || "—"}</div>
                <div className="flex items-center gap-1.5"><Icons.Calendar size={13} /> Review {fmtDate(o.review_date)}</div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center gap-1 text-sm font-medium text-[#FFB000]">Open workspace <Icons.ArrowRight size={14} /></div>
                {isSuper && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`client-actions-${o.id}`}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E4E8EF] text-[#1E2D44]/60 transition-colors hover:border-[#FFC24D] hover:text-[#0B1729]"
                        title="Client settings"
                      >
                        <Icons.MoreHorizontal size={16} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setManageAccessFor(o); }} data-testid={`manage-access-${o.id}`}>
                        <Icons.SlidersHorizontal size={15} className="mr-2" /> Manage access
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setApplyPackTo(o); }} data-testid={`apply-pack-${o.id}`}>
                        <Icons.PackageOpen size={15} className="mr-2" /> Apply onboarding pack
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && <CreateClientDialog consultants={consultants} onClose={() => setCreating(false)} onSaved={(o) => { setOrgs([...orgs, o]); setCreating(false); }} />}
      {managingPacks && <PackManagerDialog onClose={() => setManagingPacks(false)} />}
      {applyPackTo && <ApplyPackDialog org={applyPackTo} onClose={() => setApplyPackTo(null)} />}
      {manageAccessFor && (
        <Dialog open onOpenChange={() => setManageAccessFor(null)}>
          <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="manage-access-dialog">
            <DialogHeader>
              <DialogTitle>Manage access — {manageAccessFor.company_name}</DialogTitle>
              <DialogDescription>Control which areas this client's own users can see. Changes apply immediately.</DialogDescription>
            </DialogHeader>
            <ModuleAccessEditor orgId={manageAccessFor.id} orgName={manageAccessFor.company_name} />
            <DialogFooter>
              <Button onClick={() => setManageAccessFor(null)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="close-manage-access">Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function CreateClientDialog({ consultants, onClose, onSaved }) {
  const [form, setForm] = useState({ company_name: "", industry: "", employees: "", primary_contact: "", email: "", assigned_consultant_id: "", status: "onboarding", starter_resource_ids: [], onboarding_pack_id: null });
  const [resources, setResources] = useState([]);
  const [packs, setPacks] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/resources").then((r) => setResources(r.data)).catch(() => {});
    api.get("/onboarding-packs").then((r) => setPacks(r.data)).catch(() => {});
  }, []);
  const toggleRes = (id) => setForm((f) => ({ ...f, starter_resource_ids: f.starter_resource_ids.includes(id) ? f.starter_resource_ids.filter((x) => x !== id) : [...f.starter_resource_ids, id] }));
  const applyPack = (packId) => {
    const pack = packs.find((p) => p.id === packId);
    if (!pack) return;
    const packIds = (pack.resources || []).map((r) => r.id);
    setForm((f) => ({ ...f, onboarding_pack_id: packId, starter_resource_ids: Array.from(new Set([...f.starter_resource_ids, ...packIds])) }));
    toast.success(`Selected ${packIds.length} resource(s) from "${pack.name}" — assigned when you create the client`);
  };
  const save = async () => {
    if (!form.company_name) return toast.error("Add a company name");
    setSaving(true);
    try {
      const { data } = await api.post("/organisations", { ...form, assigned_consultant_id: form.assigned_consultant_id || null });
      toast.success("Client created");
      onSaved(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader><DialogTitle>New client organisation</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Create a new client organisation and optionally assign starter resources.</DialogDescription>
        <div className="space-y-3.5">
          <div><Label>Company name</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1" data-testid="client-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Industry</Label><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="mt-1" /></div>
            <div><Label>Employees</Label><Input value={form.employees} onChange={(e) => setForm({ ...form, employees: e.target.value })} className="mt-1" /></div>
            <div><Label>Primary contact</Label><Input value={form.primary_contact} onChange={(e) => setForm({ ...form, primary_contact: e.target.value })} className="mt-1" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
          </div>
          <div><Label>Assigned consultant</Label>
            <Select value={form.assigned_consultant_id} onValueChange={(v) => setForm({ ...form, assigned_consultant_id: v })}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Assign" /></SelectTrigger>
              <SelectContent>{consultants.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {packs.length > 0 && (
            <div><Label>Start from an onboarding pack <span className="font-normal text-[#1E2D44]/45">(optional)</span></Label>
              <Select value={form.onboarding_pack_id ?? undefined} onValueChange={applyPack}>
                <SelectTrigger className="mt-1" data-testid="client-pack-select"><SelectValue placeholder="Apply a saved pack…" /></SelectTrigger>
                <SelectContent>{packs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.resource_count} resource{p.resource_count === 1 ? "" : "s"}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Starter resources <span className="font-normal text-[#1E2D44]/45">(optional — assign straight away)</span></Label>
            {resources.length === 0 ? (
              <p className="mt-1 text-xs text-[#1E2D44]/45">No resources available yet.</p>
            ) : (
              <div className="mt-1.5 max-h-44 space-y-1.5 overflow-y-auto rounded-lg border border-[#E4E8EF] p-2" data-testid="starter-resources">
                {resources.map((r) => (
                  <label key={r.id} className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-[#F4F6F9]" data-testid={`starter-resource-${r.id}`}>
                    <input type="checkbox" checked={form.starter_resource_ids.includes(r.id)} onChange={() => toggleRes(r.id)} className="h-4 w-4 accent-[#FFB000]" />
                    <span className="min-w-0 flex-1 truncate text-sm text-[#0B1729]">{r.title}</span>
                    <span className="shrink-0 text-[10px] text-[#1E2D44]/45">{r.category}</span>
                  </label>
                ))}
              </div>
            )}
            {form.starter_resource_ids.length > 0 && <p className="mt-1 text-xs text-[#FFB000]">{form.starter_resource_ids.length} resource(s) will be assigned</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-client-btn">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Create client"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
